import React, { useEffect, useState } from "react";
import clsx from "clsx";
import isSameDay from "date-fns/isSameDay";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import DateFnsUtils from "@date-io/date-fns";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import CloseIcon from "@material-ui/icons/Close";
import { Calendar, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import useStyles from "./styles";

interface Props {
  stablingEntry: racehorse360.IStableEntry;
  isOpen: boolean;
  onClose: () => void;
  onChange: (stablingEntry: racehorse360.IStableEntry, date: Date) => void;
}

const DatePopup = (props: Props) => {
  const { stablingEntry = {}, isOpen, onClose, onChange } = props;
  const classes = useStyles();

  const [calendarDate, setCalendarDate] = useState<Date>(
    stablingEntry.raceReadyDate
      ? parseISO(stablingEntry.raceReadyDate)
      : new Date()
  );
  const [pickedDate, setPickedDate] = useState<Date>(
    stablingEntry.raceReadyDate ? parseISO(stablingEntry.raceReadyDate) : null
  );

  const handleRemoveDateClick = () => {
    onChange(stablingEntry, undefined);
    onClose();
  };

  const handleChange = newDate => {
    if (!isSameDay(newDate, pickedDate)) {
      setPickedDate(newDate);
      setCalendarDate(newDate);
      onChange(stablingEntry, newDate);
      onClose();
      return;
    }
  };

  useEffect(() => {
    if (stablingEntry.raceReadyDate) {
      setPickedDate(parseISO(stablingEntry.raceReadyDate));
      setCalendarDate(parseISO(stablingEntry.raceReadyDate));
    }
  }, [stablingEntry.raceReadyDate]);

  useEffect(() => {
    if (isOpen) {
      setCalendarDate(
        stablingEntry.raceReadyDate
          ? parseISO(stablingEntry.raceReadyDate)
          : new Date()
      );
      setPickedDate(
        stablingEntry.raceReadyDate
          ? parseISO(stablingEntry.raceReadyDate)
          : null
      );
    }
  }, [isOpen]);

  return (
    <Dialog
      classes={{
        root: classes.dialogRoot,
        paper: classes.dialogPaper
      }}
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle className={classes.dialogTitle}>
        {stablingEntry?.horse?.name || stablingEntry?.customHorseName}
        <IconButton
          color="primary"
          onClick={onClose}
          size={"small"}
          classes={{
            root: classes.dialogCloseButton
          }}
        >
          <CloseIcon className={classes.dialogCloseButtonIcon} />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Calendar
            date={calendarDate}
            onChange={handleChange}
            renderDay={(
              day: MaterialUiPickersDate,
              selectedDate: MaterialUiPickersDate,
              dayInCurrentMonth: boolean,
              dayComponent: JSX.Element
            ) => {
              if (isSameDay(selectedDate, day)) {
                return (
                  <Button
                    className={clsx(classes.customDateButton, {
                      [classes.customDateButtonSelected]: Boolean(pickedDate)
                    })}
                  >
                    {day.getDate()}
                  </Button>
                );
              }
              return dayComponent;
            }}
            disablePast
          />
        </MuiPickersUtilsProvider>
        <div className={classes.customDatePreview}>
          <span className={classes.customDatePreviewTitle}>
            {pickedDate
              ? "Estimated Race Ready Date"
              : "Select an Estimated Race Ready Date"}
          </span>
          <span
            className={clsx({
              [classes.customDatePreviewDate]: pickedDate,
              [classes.customDatePreviewDateFade]: !pickedDate
            })}
          >
            {!pickedDate
              ? "No Date Selected"
              : format(pickedDate, "MM/dd/yyyy")}
          </span>
        </div>
        <div className={classes.dialogButtons}>
          <Button
            className={classes.dialogButton}
            onClick={onClose}
            variant={"outlined"}
          >
            CANCEL
          </Button>
          <Button
            className={clsx(classes.dialogButtonsRemove, classes.dialogButton)}
            disabled={!stablingEntry.raceReadyDate}
            onClick={handleRemoveDateClick}
          >
            REMOVE DATE
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DatePopup;
